import React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { ReactComponent as Burger } from "../../assets/images/svg/icon-menu.svg";
import { SvgIcon } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Auth from "../../libs/Auth";
import { currentMunicipalityInterface } from "../../App";
import Footer from "../../components/Layout/Footer/Footer";
import { couleurs, couleursAdmin } from "../../theme/tokens";
import { brandingUrl } from "../../libs/branding";

const drawerWidth = 225;

interface userInterface {
    id: number;
    created_at: string;
    firstname: string;
    lastname: string;
    email: string;
    role: string;
}

type Props = {
    title: string;
    children: React.ReactNode;
    window?: () => Window;
    currentuser?: userInterface;
    municipality: currentMunicipalityInterface;
};

export default function Sidebar({
    children,
    currentuser,
    municipality,
}: Props) {
    const [mobileOpen, setMobileOpen] = React.useState(false);

    const logout = () => {
        const auth = new Auth();
        auth.logout();
        navigate("/login");
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const navigate = useNavigate();

    const drawer = (
        <>
            <Toolbar
                className="side-navigation--header"
                sx={{ padding: "0", position: "relative" }}
            >
                <Box
                    component="img"
                    src={brandingUrl(municipality.logo2)}
                    sx={{
                        backgroundColor: "#fff",
                        width: { xs: "100%", sm: "100%" },
                        height: { xs: "85%", sm: "100%" },
                        paddingTop: { xs: "1rem", sm: "0" },
                        paddingBottom: { xs: "1rem", sm: "0" },
                        objectFit: "cover",
                    }}
                />
            </Toolbar>
            <List
                className="side-navigation--nav dashboard-navigation"
                component="nav"
                sx={{
                    textTransform: "uppercase",
                    color: "#fff",
                    backgroundColor: couleurs.BleuMarin,
                    flexGrow: 1,
                    height: "90vh",
                    overflow: "auto",
                }}
            >
                <ListItem disablePadding>
                    <ListItemButton>
                        <ListItemText
                            primary={"Accueil"}
                            onClick={() => navigate("/")}
                        />
                    </ListItemButton>
                </ListItem>
                {currentuser && currentuser.id ? (
                    <>
                        <ListItem disablePadding>
                            <ListItemButton>
                                <ListItemText
                                    primary={"Mon Profil"}
                                    onClick={() => navigate("/mon-profil")}
                                />
                            </ListItemButton>
                        </ListItem>
                        {currentuser.role !== "CITIZEN" && (
                            <ListItem disablePadding>
                                <ListItemButton>
                                    <ListItemText
                                        primary={"Administration"}
                                        onClick={() => navigate("/dashboard")}
                                    />
                                </ListItemButton>
                            </ListItem>
                        )}
                        <ListItem disablePadding>
                            <ListItemButton>
                                <ListItemText
                                    primary={"Déconnexion"}
                                    onClick={() => logout()}
                                />
                            </ListItemButton>
                        </ListItem>
                    </>
                ) : (
                    <>
                        <ListItem disablePadding>
                            <ListItemButton>
                                <ListItemText
                                    primary={"Connexion"}
                                    onClick={() => navigate("/login")}
                                />
                            </ListItemButton>
                        </ListItem>
                    </>
                )}
            </List>
            <Box
                component="img"
                src={"/images/logo-dashboard-2.png"}
                sx={{ px: 3, pb: 3, backgroundColor: couleurs.BleuMarin }}
            />
        </>
    );

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />
            <AppBar
                position="absolute"
                sx={{
                    width: { lg: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar className="topbar" sx={{ backgroundColor: couleurs.GrisTresClair }}>
                    <Box className="topbar-burger">
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { lg: "none" } }}
                        >
                            <SvgIcon component={Burger} inheritViewBox />
                        </IconButton>
                    </Box>

                    <Box className="topbar-city">
                        <Box
                            component="img"
                            src={brandingUrl(municipality.logo2)}
                            sx={{
                                px: 3.125,
                                pb: 3,
                                backgroundColor: "#fff",
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    </Box>

                    <Box className="topbar-content">
                        <div className="topbar-content__banner">
                            {/* Classe dédiée : la règle SCSS `.topbar-content__banner img`
                                visait cette illustration, mais un sélecteur par balise
                                touchait AUSSI les badges plus bas (même sous-arbre DOM) —
                                ils héritaient de `max-width:170px`/`height:100%` et se
                                retrouvaient étirés et flous. Isolée ici pour que la
                                règle ne cible plus qu'elle (voir _topbar-page.scss). */}
                            <img
                                src="/images/gars-cellulaire.png"
                                alt=""
                                className="banner-illustration"
                            />
                            <Box sx={{ margin: "0 21px" }}>
                                <Typography
                                    sx={{ color: couleurs.BleuMarin, fontSize: "20px" }}
                                    className="first"
                                >
                                    <strong>Téléchargez gratuitement l'application mobile</strong>
                                </Typography>
                                <Typography className="second" sx={{ color: couleurs.BleuClair }}>
                                    et profitez d'un lien particulier avec votre municipalité
                                </Typography>
                                {/* Purement visuels pour l'instant, sans lien vers les
                                    stores (l'app n'y est pas encore publiée). */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "6px" }}>
                                    <Box
                                        component="img"
                                        src="/images/badge-app-store.png"
                                        alt="Disponible sur l'App Store"
                                        sx={{ height: { xs: 20, lg: 28 }, width: "auto", maxWidth: "none", objectFit: "contain", display: "block" }}
                                    />
                                    <Box
                                        component="img"
                                        src="/images/badge-google-play.png"
                                        alt="Disponible sur Google Play"
                                        sx={{ height: { xs: 20, lg: 28 }, width: "auto", maxWidth: "none", objectFit: "contain", display: "block" }}
                                    />
                                </Box>
                            </Box>
                        </div>
                        {!(currentuser && currentuser.id) && (
                            <div className="topbar-content__action">
                                <button
                                    className="btn btn-yellow"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/s-enregistrer")}
                                >
                                    Inscrivez-vous gratuitement !
                                </button>
                            </div>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { lg: drawerWidth }, flexShrink: { sm: 0 } }}
                aria-label="mailbox folders"
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: "block", lg: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: "none", lg: "block" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                className={"public"}
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                }}
            >
                <Box
                    className="wrapper"
                    sx={{
                        backgroundColor: couleursAdmin.FondPage,
                        flexGrow: 1,
                        overflow: "auto",
                        // marginTop: {xs:'65px', md:'0'},
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {children}
                    <Footer />
                </Box>
            </Box>
        </Box>
    );
}
